import { apiStore } from '@data';
import * as Sentry from '@sentry/browser';
import React, { ReactElement, useCallback, useEffect } from 'react';
import { Toaster, toast } from 'sonner';

import type { IError } from '@data';
import { debounce } from './helpers/debounce';

// The statuses the API itself keeps out of Sentry (`unreportedStatuses` in apps/api/src/app.ts): a
// request it refused on purpose is an answer, not a defect. A failure with no status never reached it.
const refusals = [400, 401, 403];

const isWorthReporting = (error: IError | null): boolean => error?.status != null && !refusals.includes(error.status);

const NotificationProvider = (): ReactElement => {
  const dispatchErrorNotification = useCallback(() => {
    const { error } = apiStore.getState();
    if (isWorthReporting(error)) {
      Sentry.captureException(error?.detail);
    }
    toast.error(String(error?.detail ?? ''));
  }, []);

  const debounceSuccess = debounce(toast.success, 1000);

  const dispatchSuccessNotification = useCallback(() => {
    const { success } = apiStore.getState();
    debounceSuccess(success?.message);
  }, []);

  useEffect(() => {
    const unsubFromError = apiStore.subscribe(dispatchErrorNotification, state => state.error);

    const unsubFromSuccess = apiStore.subscribe(dispatchSuccessNotification, state => state.success);

    return () => {
      unsubFromError();
      unsubFromSuccess();
      apiStore.destroy();
    };
  }, []);

  return <Toaster position="bottom-right" offset={50} richColors closeButton />;
};

export default NotificationProvider;
